// The training-mode end-of-chat "Review this chat" card. It walks the
// scheduler through Phoebe's generated judgment calls one at a time —
// each asks "Should I do this again?" with a Yes/No verdict (No requires a
// note). There is deliberately no generic "overall, how did I do?" step:
// only specific questions are worth the scheduler's time, so a chat with
// no judgment calls shows no card and the run's overall label derives
// from the per-decision verdicts. Submitting writes the run's feedback
// row, so answering the debrief is the chat's review. Styled as a quiet
// white Teach Phoebe card with green accents, matching ask_user.
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';

import { SaveNoteArtifactCard } from '@apps/web/components/phoebe_chat_view/artifacts/save_note_artifact_card';
import { RUN_FEEDBACK_DOWNVOTE_PLACEHOLDER } from '@apps/web/components/shared/run_feedback/run_feedback_prompt';
import { Button } from '@apps/web/components/ui/button';
import { Textarea } from '@apps/web/components/ui/textarea';
import { cn } from '@apps/web/lib/utils';
import { type AgentRunDebriefDecisionVerdict } from '@libraries/typescript/api_client';

export type RunDebriefDecision = {
    readonly id: string;
    readonly text: string;
};

type Verdict = 'good' | 'bad';

export type RunDebriefSubmission = {
    readonly label: Verdict;
    readonly comment?: string;
    readonly decisionVerdicts: Array<AgentRunDebriefDecisionVerdict>;
};

export type RunDebriefPromptContent = {
    readonly title: string;
    readonly decisionIntro: string | null;
    readonly reasoningPoints: ReadonlyArray<string>;
};

const DEFAULT_RUN_DEBRIEF_PROMPT_CONTENT: RunDebriefPromptContent = {
    title: 'Review this chat',
    decisionIntro: null,
    reasoningPoints: [],
};

/**
 * Each step reads as a friendly, self-contained "I did X in this chat."
 * sentence. Newer debriefs are generated that way already; older ones use
 * verb-first fragments ("Texted instead of calling…"), which get an "I"
 * prefixed here so both read the same.
 */
function firstPersonDecisionText(text: string): string {
    const trimmed = text.trim();
    if (/^I\b/.test(trimmed)) {
        return trimmed;
    }
    // Lowercase a leading capitalized word ("Texted" → "texted") but leave
    // acronyms and proper-looking starts ("SMS'd") untouched.
    const lowered = /^[A-Z][a-z]/.test(trimmed)
        ? trimmed.charAt(0).toLowerCase() + trimmed.slice(1)
        : trimmed;
    return `I ${lowered}`;
}

function ReviewChatHeader() {
    return (
        <p className="text-training-accent-strong flex items-center gap-1.5 text-xs font-semibold">
            <svg
                className="teach-phoebe-sprout text-training-accent h-3.5 w-3.5 overflow-visible"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden={true}
            >
                <path
                    className="teach-phoebe-sprout-ground"
                    pathLength={100}
                    d="M5 21h14"
                />
                <path
                    className="teach-phoebe-sprout-stem"
                    pathLength={100}
                    d="M14 9.536V7a4 4 0 0 1 4-4h1.5a.5.5 0 0 1 .5.5V5a4 4 0 0 1-4 4 4 4 0 0 0-4 4c0 2 1 3 1 5a5 5 0 0 1-1 3"
                />
                <path
                    className="teach-phoebe-sprout-leaf"
                    pathLength={100}
                    d="M4 9a5 5 0 0 1 8 4 5 5 0 0 1-8-4"
                />
            </svg>
            Teach Phoebe
        </p>
    );
}

export function RunDebriefAcknowledgement({
    notes,
}: {
    readonly notes: ReadonlyArray<string>;
}) {
    return (
        <div className="animate-in fade-in-0 slide-in-from-bottom-1 flex flex-col gap-2 duration-200 motion-reduce:animate-none">
            {notes.length > 0 ? (
                notes.map((note, index) => (
                    <SaveNoteArtifactCard
                        key={index}
                        artifact={{
                            noteId: `run-debrief-note-${index}`,
                            content: note,
                        }}
                        staggerIndex={index}
                        showWhileUnresolved={true}
                    />
                ))
            ) : (
                <p className="text-muted-foreground text-sm">
                    Thanks for the feedback.
                </p>
            )}
        </div>
    );
}

function VerdictSegment({
    value,
    disabled,
    onChange,
}: {
    readonly value: Verdict | undefined;
    readonly disabled: boolean;
    readonly onChange: (verdict: Verdict) => void;
}) {
    return (
        <div className="bg-muted/60 inline-flex shrink-0 gap-0.5 rounded-full p-0.5">
            <button
                type="button"
                disabled={disabled}
                onClick={() => onChange('good')}
                aria-label="Good call"
                aria-pressed={value === 'good'}
                className={cn(
                    'rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                    value === 'good'
                        ? 'bg-training-accent/10 text-training-accent-strong ring-training-accent/25 ring-1'
                        : 'text-muted-foreground hover:text-foreground',
                )}
            >
                Yes
            </button>
            <button
                type="button"
                disabled={disabled}
                onClick={() => onChange('bad')}
                aria-label="Would do differently"
                aria-pressed={value === 'bad'}
                className={cn(
                    'rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50',
                    value === 'bad'
                        ? 'bg-card text-foreground ring-border ring-1'
                        : 'text-muted-foreground hover:text-foreground',
                )}
            >
                No
            </button>
        </div>
    );
}

export function RunDebriefCard({
    decisions,
    disabled,
    promptContent = DEFAULT_RUN_DEBRIEF_PROMPT_CONTENT,
    submitted,
    onSubmit,
    onSkip,
}: {
    readonly decisions: Array<RunDebriefDecision>;
    readonly disabled: boolean;
    readonly promptContent?: RunDebriefPromptContent;
    readonly submitted: boolean;
    readonly onSubmit: (submission: RunDebriefSubmission) => void;
    readonly onSkip: () => void;
}) {
    const [verdicts, setVerdicts] = useState<Record<string, Verdict>>({});
    const [notes, setNotes] = useState<Record<string, string>>({});
    // One judgment call per step; the last step submits.
    const [stepIndex, setStepIndex] = useState(0);
    const totalSteps = decisions.length;
    const activeDecision = decisions[stepIndex];
    const onLastStep = stepIndex >= totalSteps - 1;

    const missingDecisionNote = useMemo(
        () =>
            decisions.some(
                (decision) =>
                    verdicts[decision.id] === 'bad' &&
                    !(notes[decision.id] ?? '').trim(),
            ),
        [decisions, verdicts, notes],
    );
    const answeredCount = useMemo(
        () =>
            decisions.filter((decision) => verdicts[decision.id] !== undefined)
                .length,
        [decisions, verdicts],
    );
    const canSubmit = answeredCount > 0 && !missingDecisionNote;
    // Verdicts stay optional (matching the old all-at-once card), but a ✕
    // must carry its note before the wizard moves on.
    const activeDecisionNeedsNote =
        activeDecision !== undefined &&
        verdicts[activeDecision.id] === 'bad' &&
        !(notes[activeDecision.id] ?? '').trim();

    // Mirrors the notes the submit actually persists as agent notes: each
    // noted ✕ verdict.
    const savedNotes = useMemo(
        () =>
            decisions
                .filter((decision) => verdicts[decision.id] === 'bad')
                .map((decision) => (notes[decision.id] ?? '').trim())
                .filter((note) => note.length > 0),
        [decisions, verdicts, notes],
    );

    if (submitted) {
        return <RunDebriefAcknowledgement notes={savedNotes} />;
    }
    // No judgment calls means nothing specific to ask — show no card
    // rather than a generic rating prompt.
    if (decisions.length === 0) {
        return null;
    }

    function setVerdict(decisionId: string, verdict: Verdict) {
        setVerdicts((current) => ({ ...current, [decisionId]: verdict }));
    }

    function goToPreviousStep() {
        setStepIndex((current) => Math.max(current - 1, 0));
    }

    function goToNextStep() {
        if (activeDecisionNeedsNote) {
            return;
        }
        setStepIndex((current) => Math.min(current + 1, totalSteps - 1));
    }

    function handleSubmit() {
        if (!canSubmit) {
            return;
        }
        const decisionVerdicts: Array<AgentRunDebriefDecisionVerdict> = [];
        for (const decision of decisions) {
            const verdict = verdicts[decision.id];
            if (verdict === undefined) {
                continue;
            }
            const note = (notes[decision.id] ?? '').trim();
            decisionVerdicts.push({
                decision_id: decision.id,
                verdict,
                note: note || null,
            });
        }
        // The chat-level label derives from the specific verdicts: any
        // "would do differently" marks the chat for review.
        onSubmit({
            label: decisionVerdicts.some((entry) => entry.verdict === 'bad')
                ? 'bad'
                : 'good',
            decisionVerdicts,
        });
    }

    return (
        <div
            data-testid="run-debrief-card"
            className="bg-card border-border animate-in fade-in-0 slide-in-from-bottom-1 relative flex w-full max-w-full min-w-0 flex-col rounded-xl border p-3 shadow-sm duration-200 motion-reduce:animate-none"
        >
            <div className="flex items-center justify-between gap-3">
                <ReviewChatHeader />
                {totalSteps > 1 ? (
                    <div className="text-muted-foreground flex shrink-0 items-center gap-3 text-sm">
                        <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            aria-label="Previous"
                            disabled={stepIndex === 0}
                            onClick={goToPreviousStep}
                        >
                            <ChevronLeft className="h-4 w-4" />
                        </Button>
                        <span>
                            {stepIndex + 1} of {totalSteps}
                        </span>
                        <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            aria-label="Next"
                            disabled={
                                stepIndex >= totalSteps - 1 ||
                                activeDecisionNeedsNote
                            }
                            onClick={goToNextStep}
                        >
                            <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                ) : null}
            </div>
            {totalSteps > 1 ? (
                <div
                    className="bg-training-accent/15 mt-2 mb-3 h-0.5 overflow-hidden rounded-full"
                    aria-hidden={true}
                >
                    <div
                        className="bg-training-accent h-full rounded-full transition-[width] duration-200 motion-reduce:transition-none"
                        style={{
                            width: `${((stepIndex + 1) / totalSteps) * 100}%`,
                        }}
                    />
                </div>
            ) : null}
            <p className="text-foreground text-sm font-semibold">
                {promptContent.title}
            </p>
            {/* The keyed wrapper re-mounts on step change so each step
                slides in, mirroring the ask_user question card. */}
            <div key={stepIndex} className="teach-question-slide-in">
                {stepIndex === 0 && promptContent.reasoningPoints.length > 0 ? (
                    <ul className="text-muted-foreground mt-2 list-disc space-y-1 pl-4 text-sm">
                        {promptContent.reasoningPoints.map((point, index) => (
                            <li key={index}>{point}</li>
                        ))}
                    </ul>
                ) : null}
                {stepIndex === 0 &&
                decisions.length > 0 &&
                promptContent.decisionIntro !== null ? (
                    <p className="text-muted-foreground mt-0.5 text-sm">
                        {promptContent.decisionIntro}
                    </p>
                ) : null}
                {activeDecision !== undefined ? (
                    <div>
                        <p className="text-foreground mt-3 text-[15px] leading-snug font-medium tracking-tight text-pretty">
                            {firstPersonDecisionText(activeDecision.text)}
                        </p>
                        <div className="mt-4 flex items-center justify-between gap-3">
                            <p className="text-muted-foreground text-sm font-medium">
                                Should I do this again?
                            </p>
                            <VerdictSegment
                                value={verdicts[activeDecision.id]}
                                disabled={disabled}
                                onChange={(verdict) => {
                                    setVerdict(activeDecision.id, verdict);
                                    // A ✓ is the whole answer, so it should
                                    // not also cost a click on Next. A ✕
                                    // opens a required note, so that one
                                    // stays put. Advances on the verdict in
                                    // hand rather than re-reading `verdicts`,
                                    // which does not carry it until the next
                                    // render.
                                    if (
                                        verdict === 'good' &&
                                        stepIndex < totalSteps - 1
                                    ) {
                                        setStepIndex((current) =>
                                            Math.min(
                                                current + 1,
                                                totalSteps - 1,
                                            ),
                                        );
                                    }
                                }}
                            />
                        </div>
                        {verdicts[activeDecision.id] === 'bad' ? (
                            <Textarea
                                className="mt-3 min-h-[72px] text-sm"
                                placeholder={RUN_FEEDBACK_DOWNVOTE_PLACEHOLDER}
                                value={notes[activeDecision.id] ?? ''}
                                disabled={disabled}
                                onChange={(event) => {
                                    const decisionId = activeDecision.id;
                                    setNotes((current) => ({
                                        ...current,
                                        [decisionId]: event.target.value,
                                    }));
                                }}
                            />
                        ) : null}
                    </div>
                ) : null}
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground"
                    disabled={disabled}
                    onClick={onSkip}
                >
                    Skip
                </Button>
                {onLastStep ? (
                    <Button
                        type="button"
                        size="sm"
                        className="bg-training-accent hover:bg-training-accent-strong rounded-full text-white"
                        disabled={disabled || !canSubmit}
                        onClick={handleSubmit}
                    >
                        Submit feedback
                    </Button>
                ) : (
                    <Button
                        type="button"
                        size="sm"
                        className="bg-training-accent hover:bg-training-accent-strong rounded-full text-white"
                        disabled={disabled || activeDecisionNeedsNote}
                        onClick={goToNextStep}
                    >
                        Next
                    </Button>
                )}
            </div>
        </div>
    );
}
